import React from 'react';
import { Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { ChartDataPoint } from '../../types';
import Card from '../common/Card';

ChartJS.register(ArcElement, Tooltip, Legend);

interface PieChartProps {
  title: string;
  subtitle?: string;
  data: ChartDataPoint[];
  height?: number;
  doughnut?: boolean;
  showLegend?: boolean;
}

const PieChart: React.FC<PieChartProps> = ({
  title,
  subtitle,
  data,
  height = 300,
  doughnut = false,
  showLegend = true,
}) => {
  const isDark = document.documentElement.classList.contains('dark');

  const colors = [
    '#0ea5e9', // Primary blue
    '#06b6d4', // Cyan
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#f59e0b', // Amber
    '#10b981', // Green
    '#f43f5e', // Rose
    '#6366f1', // Indigo
  ];

  const chartData = {
    labels: data.map(d => d.label),
    datasets: [
      {
        data: data.map(d => d.value),
        backgroundColor: colors.slice(0, data.length),
        borderColor: isDark ? '#1e293b' : '#ffffff',
        borderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const options: ChartOptions<'pie' | 'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: showLegend,
        position: 'right' as const,
        labels: {
          color: isDark ? '#cbd5e1' : '#475569',
          font: {
            size: 12,
            weight: 500,
          },
          padding: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels && data.datasets.length) {
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i] as number;
                const total = (data.datasets[0].data as number[]).reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                
                return {
                  text: `${label} (${percentage}%)`,
                  fillStyle: (data.datasets[0].backgroundColor as string[])[i],
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        titleColor: isDark ? '#f1f5f9' : '#0f172a',
        bodyColor: isDark ? '#cbd5e1' : '#475569',
        borderColor: isDark ? '#334155' : '#e2e8f0',
        borderWidth: 1,
        padding: 12,
        bodyFont: {
          size: 13,
        },
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a: number, b: any) => a + (b as number), 0) as number;
            const percentage = ((value / total) * 100).toFixed(1);
            
            return [
              `${label}`,
              `Value: ${new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(value)}`,
              `Share: ${percentage}%`,
            ];
          },
        },
      },
    },
    cutout: doughnut ? '65%' : undefined,
  };

  const ChartComponent = doughnut ? Doughnut : Pie;

  return (
    <Card title={title} subtitle={subtitle} hover>
      <div style={{ height: `${height}px` }} className="chart-container flex items-center justify-center">
        <ChartComponent data={chartData} options={options} />
      </div>
    </Card>
  );
};

export default PieChart;
